import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "shared/components/ui/table";
import styles from "./performanceTable.module.scss";
import colorsStore from "shared/store/colorsStore";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

const PerformanceTable = ({ dataObject }) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const strumenti_fondi_etf = dataObject?.strumenti_fondi_etf;
  const strumenti_obbligazionari = dataObject?.strumenti_obbligazionari;
  const totalFondiPeso = useMemo(
    () =>
      strumenti_fondi_etf?.reduce((acc, curr) => acc + curr.peso, 0).toFixed(2),
    [strumenti_fondi_etf],
  );
  const totalObbligazionariPeso = useMemo(
    () =>
      strumenti_obbligazionari
        ?.reduce((acc, curr) => acc + curr.peso, 0)
        .toFixed(2),
    [strumenti_obbligazionari],
  );

  if (!strumenti_fondi_etf || !strumenti_obbligazionari) {
    return null;
  }

  const formatValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === 0 ||
      value === "0.00"
    )
      return "";
    return value.toFixed(2).replace(".", ",");
  };

  const getValueColor = (value) =>
    value < 0 ? colorsConfig.red : colorsConfig.FinecoBlack;

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableContainerInner}>
        <Table className={styles.table}>
          <TableHeader>
            <TableRow>
              <TableHead colSpan={4}></TableHead>
              <TableHead colSpan={3} className={styles.rendimento}>
                <div className={styles.border}>{t("portfolio.rendimento")}</div>
              </TableHead>
              <TableHead colSpan={2} className={styles.volatilitaAnn}>
                <div
                  className={classNames(styles.border, styles.exceptVolatilita)}
                >
                  {t("portfolio.volatilitaAnn")}
                </div>
              </TableHead>
            </TableRow>
            <TableRow>
              <TableHead
                className={classNames(styles.nameCell, styles.headerCell)}
              >
                {t("portfolio.nome")}
              </TableHead>
              <TableHead
                className={classNames(styles.currencyCell, styles.headerCell)}
              >
                {t("portfolio.divisa")}
              </TableHead>
              <TableHead
                className={classNames(styles.weightCell, styles.headerCell)}
              >
                {t("portfolio.peso")}
              </TableHead>
              <TableHead
                className={classNames(
                  styles.microCell,
                  styles.headerCellCenter,
                )}
              >
                {t("portfolio.microcategoriaAdvice")}
              </TableHead>
              <TableHead className={styles.numericCell}>YTD</TableHead>
              <TableHead className={styles.numericCell}>1Y</TableHead>
              <TableHead className={styles.numericCell}>3Y</TableHead>
              <TableHead className={styles.numericCell}>1Y</TableHead>
              <TableHead className={styles.numericCell}>3Y</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {strumenti_fondi_etf.length > 0 && (
              <TableRow className={styles.totalRow}>
                <TableCell style={{ fontWeight: 700 }}>
                  {t("portfolio.fondiAndETF")}
                </TableCell>
                <TableCell></TableCell>
                <TableCell className={styles.textCenter}>
                  {totalFondiPeso}
                </TableCell>
                <TableCell colSpan={6}></TableCell>
              </TableRow>
            )}
            {strumenti_fondi_etf.map((row, index) => (
              <TableRow key={index}>
                <TableCell className={styles.nameCell}>{row.nome}</TableCell>
                <TableCell className={styles.currencyCell}>
                  {row.divisa}
                </TableCell>
                <TableCell className={styles.weightCell}>
                  {formatValue(row.peso)}
                </TableCell>
                <TableCell
                  className={classNames(styles.microCell, styles.textCenter)}
                >
                  {row.microcategoria_advice}
                </TableCell>
                <TableCell
                  className={styles.numericCellValue}
                  style={{ color: getValueColor(row.rendimento_YTD) }}
                >
                  {formatValue(row.rendimento_YTD)}
                </TableCell>
                <TableCell
                  className={styles.numericCellValue}
                  style={{ color: getValueColor(row.rendimento_1Y) }}
                >
                  {formatValue(row.rendimento_1Y)}
                </TableCell>
                <TableCell
                  className={styles.numericCellValue}
                  style={{ color: getValueColor(row.rendimento_3Y) }}
                >
                  {formatValue(row.rendimento_3Y)}
                </TableCell>
                <TableCell
                  className={styles.numericCellValue}
                  style={{ color: getValueColor(row.volatilita_1Y) }}
                >
                  {formatValue(row.volatilita_1Y)}
                </TableCell>
                <TableCell
                  className={styles.numericCellValue}
                  style={{ color: getValueColor(row.volatilita_3Y) }}
                >
                  {formatValue(row.volatilita_3Y)}
                </TableCell>
              </TableRow>
            ))}
            {strumenti_obbligazionari.length > 0 && (
              <>
                <TableRow>
                  <TableCell
                    className={styles.emptyCell}
                    colSpan={9}
                  ></TableCell>
                </TableRow>
                <TableRow className={styles.totalRow}>
                  <TableCell style={{ fontWeight: 700 }}>
                    {t("portfolio.obbligazioni")}
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell className={styles.textCenter}>
                    {totalObbligazionariPeso}
                  </TableCell>
                  <TableCell colSpan={6}></TableCell>
                </TableRow>
              </>
            )}
            {strumenti_obbligazionari.map((row, index) => (
              <TableRow key={index}>
                <TableCell className={styles.nameCell}>{row.nome}</TableCell>
                <TableCell className={styles.currencyCell}>
                  {row.divisa}
                </TableCell>
                <TableCell className={styles.weightCell}>
                  {formatValue(row.peso)}
                </TableCell>
                <TableCell
                  className={classNames(styles.microCell, styles.textCenter)}
                >
                  {row.microcategoria_advice}
                </TableCell>
                <TableCell
                  className={styles.numericCellValue}
                  style={{ color: getValueColor(row.rendimento_YTD) }}
                >
                  {formatValue(row.rendimento_YTD)}
                </TableCell>
                <TableCell
                  className={styles.numericCellValue}
                  style={{ color: getValueColor(row.rendimento_1Y) }}
                >
                  {formatValue(row.rendimento_1Y)}
                </TableCell>
                <TableCell
                  className={styles.numericCellValue}
                  style={{ color: getValueColor(row.rendimento_3Y) }}
                >
                  {formatValue(row.rendimento_3Y)}
                </TableCell>
                <TableCell
                  className={styles.numericCellValue}
                  style={{ color: getValueColor(row.volatilita_1Y) }}
                >
                  {formatValue(row.volatilita_1Y)}
                </TableCell>
                <TableCell
                  className={styles.numericCellValue}
                  style={{ color: getValueColor(row.volatilita_3Y) }}
                >
                  {formatValue(row.volatilita_3Y)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PerformanceTable;
